import type { SeatLayoutDefinition, SeatLayoutSection } from "./seat-layouts";

export function sectionPathSlug(section: SeatLayoutSection): string {
	const names: Record<string, string> = {
		전체: "all",
		중앙: "center",
		좌측: "left",
		우측: "right",
	};
	const slug =
		names[section.shortLabel] ??
		(/^[a-z]+$/i.test(section.shortLabel)
			? section.shortLabel.toLowerCase()
			: null);
	if (!slug) throw new Error(`Unknown section path: ${section.shortLabel}`);
	return slug;
}
export function sectionFromPath(
	layout: SeatLayoutDefinition,
	floorId: string,
	slug: string,
) {
	return layout.floors
		.find((floor) => floor.id === floorId)
		?.sections.find((section) => sectionPathSlug(section) === slug);
}
export function rowPath(
	layout: SeatLayoutDefinition,
	venueSlug: string,
	sectionId: string,
	rowLabel: string,
): string | null {
	const floor = layout.floors.find((floor) =>
		floor.sections.some(
			(section) => section.id === sectionId || section.label === sectionId,
		),
	);
	const section = floor?.sections.find(
		(section) => section.id === sectionId || section.label === sectionId,
	);
	return floor && section
		? `/venue/${venueSlug}/${layout.spaceId}/${floor.id}/${sectionPathSlug(section)}/${encodeURIComponent(rowLabel)}`
		: null;
}
function rowContext(
	layout: SeatLayoutDefinition,
	sectionId: string,
	rowLabel: string,
) {
	const floor = layout.floors.find((floor) =>
		floor.sections.some((s) => s.id === sectionId || s.label === sectionId),
	);
	const section = floor?.sections.find(
		(s) => s.id === sectionId || s.label === sectionId,
	);
	const index = section?.rows?.findIndex((row) => row.label === rowLabel) ?? -1;
	return { floor, section, index, row: section?.rows?.[index] };
}

/** Official facts shared by every seat in a row; never borrow its first seat. */
export function rowFacts(
	layout: SeatLayoutDefinition,
	sectionId: string,
	rowLabel: string,
) {
	const { floor, section, index, row } = rowContext(layout, sectionId, rowLabel);
	if (!floor || !section)
		return {
			lines: ["공식 자료에서 이 구역을 확인하지 못했어요."],
			source: layout.source,
			known: false,
		};
	const position = {
		left: "무대 왼쪽",
		center: "무대 정면",
		right: "무대 오른쪽",
		full: "전체 구역",
	}[section.position];
	const lines = [`${floor.label} · ${section.shortLabel}구역 · ${position}`];
	if (row) {
		lines.push(`${section.rows?.length}개 열 중 앞에서 ${index + 1}번째`);
		if (row.gapBefore) lines.push("가로 통로 바로 뒤 열이에요.");
	}
	return { lines, source: layout.source, known: Boolean(row) };
}

export function seatFacts(
	layout: SeatLayoutDefinition,
	sectionId: string,
	rowLabel: string,
	seatNumber: number,
) {
	const facts = rowFacts(layout, sectionId, rowLabel);
	const { row } = rowContext(layout, sectionId, rowLabel);
	const lines = [...facts.lines];
	const seatIndex = row?.seats.indexOf(seatNumber) ?? -1;
	const accessible = Boolean(row?.accessibleSeats?.includes(seatNumber));
	const aisle = Boolean(
		row?.aisleAfter?.some(
			(number) =>
				number === seatNumber ||
				row.seats[row.seats.indexOf(number) + 1] === seatNumber,
		),
	);
	if (row && seatIndex >= 0) {
		const offset = Math.abs(seatIndex - (row.seats.length - 1) / 2);
		lines.push(
			`이 열 ${row.seats.length}석 중 ${offset <= 0.5 ? "가운데" : `가운데에서 ${Math.round(offset)}석 옆`}`,
		);
		if (aisle) lines.push("통로 옆이에요.");
		if (accessible) lines.push("공식 휠체어석이에요.");
	}
	return {
		lines,
		source: layout.source,
		accessible,
		aisle,
		known: Boolean(row && seatIndex >= 0),
	};
}
export function cinemaPosition(
	row: number,
	seat: number,
	totalRows: number,
	totalSeats: number,
): string | null {
	if (
		![row, seat, totalRows, totalSeats].every(
			(n) => Number.isInteger(n) && n > 0,
		) ||
		row > totalRows ||
		seat > totalSeats
	)
		return null;
	return `앞에서 ${Math.round((row / totalRows) * 100)}% · 가운데에서 ${Math.round(Math.abs(seat - (totalSeats + 1) / 2))}석 옆`;
}
